import { useContext, useEffect, useState } from "react";
import {
  StrategyTabs,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import { BotType, LanguageNameSpaces, OverrideFieldType } from "@shared/types";
import { OrderTypes } from "@shared/features/trades/common/types";
import {
  BotFormContext,
  PreserveBotFormValuesContext,
} from "@shared/features/bots/BotForm/common/BotForm.context";
import { isSymbol } from "@shared/types/utils";
import {
  useDefaultStrategyValue,
  useStrategyError,
} from "@shared/features/bots/BotForm/common/hooks";
import { getStrategyTab } from "@shared/features/bots/BotForm/common/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

export interface UseStrategiesProps {
  orderType: OrderTypes.ENTRY | OrderTypes.TAKE_PROFIT;
}

const useStrategies = ({ orderType }: UseStrategiesProps) => {
  const preserveValues = useContext(PreserveBotFormValuesContext);
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    isEdit,
    entryStrategy,
    takeProfitStrategy,
    initialized: botFormInitialized,
    defaultTakeProfit,
    botType,
    checkedFields,
    fieldsToInitialize,
    isChannelConfiguration,
  } = useContext(BotFormContext)!;
  const [isInitialized, setIsInitialized] = useState(!isEdit);
  const getDefaultStrategyValue = useDefaultStrategyValue(orderType);

  const isEntry = orderType === OrderTypes.ENTRY;
  const fieldName = isEntry ? "entryStrategy" : "takeProfitStrategy";
  const shouldInitializeField = fieldsToInitialize?.has(fieldName);
  const errorMessage = useStrategyError(orderType);
  const isSignalsBot = botType === BotType.SIGNAL;
  const isHedgingBot = botType === BotType.HEDGING;
  const strategy = isEntry ? entryStrategy : takeProfitStrategy;

  const getDefaultTab = () => {
    if (preserveValues && strategy) return getStrategyTab(strategy, botType);
    if (isEdit) {
      if (botFormInitialized) {
        return getStrategyTab(strategy, botType);
      }
      return;
    } else if (!isEdit && isSignalsBot) {
      return StrategyTabs.BUILT_IN;
    }
    return StrategyTabs.DCA;
  };

  const [tab, setTab] = useState<StrategyTabs | undefined>(getDefaultTab());
  const strategyTab = getStrategyTab(strategy, botType);

  let finalTab = tab;
  if (shouldInitializeField && strategyTab) {
    finalTab = strategyTab;
  }

  useEffect(() => {
    if (strategyTab && strategyTab !== tab) {
      onTabChange(strategyTab);
    }
  }, [strategyTab]);

  useEffect(() => {
    if (isEdit && botFormInitialized) {
      setTab(getDefaultTab());
    }
  }, [isEdit, botFormInitialized]);

  const onTabChange = (tab: string) => {
    setTab(tab as StrategyTabs);
  };

  const isSignalsOrTradingViewBot = [
    BotType.TRADING_VIEW,
    BotType.TRADING_VIEW_SIGNALS,
    BotType.SIGNAL,
  ].includes(botType);
  const isMultiToggleValue =
    isSymbol(strategy) || isSymbol(strategy?.strategy_style);
  const showTakeProfitMultiValue =
    !isEntry &&
    !!defaultTakeProfit &&
    !isSymbol(defaultTakeProfit) &&
    checkedFields.has(ToggleField.TAKE_PROFITS);
  const showMultiToggleOverlay =
    !isSignalsOrTradingViewBot &&
    !isHedgingBot &&
    isMultiToggleValue &&
    (isEntry || showTakeProfitMultiValue);

  const ratiosTitle = isEntry
    ? t("labels.entries_ratios")
    : t("labels.take_profits_ratios");
  const ratiosToggleFieldData = {
    isToggleField: isSignalsBot,
    title: ratiosTitle,
    fieldType: isEntry
      ? ToggleField.ENTRY_RATIOS
      : ToggleField.TAKE_PROFIT_RATIOS,
    overrideObjectName: ratiosTitle,
    overrideFieldType: isEntry
      ? OverrideFieldType.ENTRY_STRATEGY
      : OverrideFieldType.TAKE_PROFIT_STRATEGY,
  };

  const showFirstTakeProfitDistance =
    !isEntry && isSignalsBot && !isChannelConfiguration;
  const firstTakeProfitDistanceData = {
    shouldDisplay: showFirstTakeProfitDistance,
    fieldType: ToggleField.TAKE_PROFITS,
    title: t("labels.first_take_profit_distance"),
    getDefaultValue: () => "1",
  };

  return {
    errorMessage,
    isInitialized,
    setIsInitialized,
    onTabChange,
    tab: finalTab,
    isMultiToggleValue,
    showMultiToggleOverlay,
    getDefaultValue: getDefaultStrategyValue,
    ratiosToggleFieldData,
    firstTakeProfitDistanceData,
  };
};

export default useStrategies;
